/**
 * Live, running score — the number that ticks upward while someone is singing.
 *
 * ── Why this is not just scoreTake() on a timer ──────────────────────────────
 * `scoreTake` walks every frame and, worse, cross-correlates ±400 ms to find the
 * device offset. Calling it 30 times a second on a 4-minute song is ~190k operations
 * per call on a phone that is also decoding audio and running YIN. It would stutter
 * the very take it is measuring.
 *
 * So the components are kept as running sums and updated in O(1) per frame. The
 * arithmetic is exactly what `scoreTake` does — mean pitch mark, mask agreement,
 * completeness, and a Pearson correlation built from sums rather than from a stored
 * array of pairs. Both call `combineScore()`, so there is one formula and the live
 * ticker cannot disagree with the score that gets recorded. `scripts/scoring.test.mjs`
 * asserts the two produce identical output on identical frames.
 *
 * ── The offset ───────────────────────────────────────────────────────────────
 * Device latency is not the singer's fault and has to come out before timing means
 * anything. It cannot be known from the first frame, so it is estimated once there
 * is enough audio to estimate it from, and again a little later; each re-estimate
 * replays the accumulators over the frames collected so far. That is O(n) but happens
 * twice in a take, not thirty times a second.
 */
import { TRACK } from './pitchTrack.mjs';
import {
  centsApart, pitchAccuracy, combineScore, pearsonFromSums, gradeFor,
  decodeContour, estimateOffsetSec, LiveMeter,
} from './scoring.mjs';

/** Seconds of audio before the first offset estimate, and when to refine it. */
const OFFSET_AT = [8, 25];
/** Below this the take is not scored at all — the silent-mic case. */
const MIN_VOICED_RATIO = 0.05;
/** Enough co-voiced frames to say anything at all (≈ 0.6s of singing). */
const MIN_PAIRS = 20;

export class RunningScore {
  constructor(contour, { windowSec = 3 } = {}) {
    this.contour = contour;
    this.ref = decodeContour(contour);
    this.hopSec = contour.hopSec ?? TRACK.hop / TRACK.sampleRate;
    this.meter = new LiveMeter(contour, { windowSec });

    this.frames = [];        // every singer frame, in song time
    this.offsetSec = 0;
    this.offsetsDone = 0;
    this.reset();
  }

  reset() {
    this.acc = {
      pitchSum: 0,
      pitchN: 0,
      sung: 0,
      expected: 0,
      agree: 0,
      maskN: 0,
      // Welford, for the spread of the cents deviations
      devN: 0,
      devMean: 0,
      devM2: 0,
      // Pearson sums over co-voiced [singerMidi, refMidi]
      n: 0, sx: 0, sy: 0, sxx: 0, syy: 0, sxy: 0,
    };
    this.lastIdx = -1;
  }

  /** Fold one singer frame into the accumulators against reference index `i`. */
  #apply(frame, i) {
    const r = this.ref.frames[i];
    if (!r) return;
    const a = this.acc;

    const refVoiced = r.midi != null;
    const sngVoiced = !!(frame && frame.midi != null);
    if (refVoiced || sngVoiced) {
      a.maskN += 1;
      if (refVoiced === sngVoiced) a.agree += 1;
    }
    if (!refVoiced) return;

    a.expected += 1;
    if (!sngVoiced) return;
    a.sung += 1;

    const cents = centsApart(frame.midi, r.midi);
    a.pitchSum += pitchAccuracy(cents);
    a.pitchN += 1;

    a.devN += 1;
    const delta = cents - a.devMean;
    a.devMean += delta / a.devN;
    a.devM2 += delta * (cents - a.devMean);

    const x = frame.midi;
    const y = r.midi;
    a.n += 1; a.sx += x; a.sy += y; a.sxx += x * x; a.syy += y * y; a.sxy += x * y;
  }

  /** Replay every frame collected so far. Used only when the offset changes. */
  #rebuild() {
    this.reset();
    const indexed = [];
    for (const f of this.frames) indexed[Math.round(f.t / this.hopSec)] = f;
    const last = Math.round((this.frames[this.frames.length - 1]?.t ?? 0) / this.hopSec);
    for (let i = 0; i <= last; i += 1) {
      this.#apply(indexed[Math.round(i + this.offsetSec / this.hopSec)], i);
    }
    this.lastIdx = last;
  }

  /**
   * Feed one tracked frame. `frame.t` is seconds since the backing track started.
   * Frames must arrive in order, which the capture path guarantees.
   */
  push(frame) {
    this.frames.push(frame);
    this.meter.push(frame);

    const elapsed = frame.t;
    if (this.offsetsDone < OFFSET_AT.length && elapsed >= OFFSET_AT[this.offsetsDone]) {
      this.offsetsDone += 1;
      const indexed = [];
      for (const f of this.frames) indexed[Math.round(f.t / this.hopSec)] = f;
      this.offsetSec = estimateOffsetSec(this.ref.frames, indexed, this.hopSec);
      this.#rebuild();
      return;
    }

    // The reference index this frame lines up with once latency is removed.
    const i = Math.round((frame.t - this.offsetSec) / this.hopSec);
    if (i <= this.lastIdx || i < 0) return;   // out of order or before the song
    this.#apply(frame, i);
    this.lastIdx = i;
  }

  /** Current standing. Cheap — safe to call on every animation frame. */
  read() {
    const a = this.acc;
    const voicedRatio = this.frames.length
      ? this.frames.filter((f) => f.midi != null).length / this.frames.length
      : 0;

    const base = {
      offsetMs: Math.round(this.offsetSec * 1000),
      voicedRatio: Number(voicedRatio.toFixed(3)),
      ...this.meter.read(this.frames[this.frames.length - 1]?.t ?? 0),
    };

    if (a.pitchN < MIN_PAIRS || a.expected === 0) {
      return {
        ...base, score: 0, grade: 'C', pitch: 0, timing: 0, completeness: 0, fit: 0,
        settled: false,
      };
    }

    const pitch = a.pitchSum / a.pitchN;
    const completeness = a.sung / a.expected;
    const maskAgreement = a.maskN > 0 ? a.agree / a.maskN : 0;
    const spread = a.devN > 1 ? Math.sqrt(a.devM2 / a.devN) : 0;
    const timing = Math.max(0, Math.min(1, maskAgreement * (1 - Math.min(1, spread / 600))));
    const fit = Math.max(0, pearsonFromSums(a));

    const score = (voicedRatio < MIN_VOICED_RATIO)
      ? 0
      : combineScore({ pitch, timing, completeness, fit });

    return {
      ...base,
      score,
      grade: gradeFor(score),
      pitch: Number(pitch.toFixed(3)),
      timing: Number(timing.toFixed(3)),
      completeness: Number(completeness.toFixed(3)),
      fit: Number(fit.toFixed(3)),
      // The offset is only trustworthy after the first estimate; until then the
      // timing component is provisional and the UI says so rather than showing a
      // number that will visibly jump.
      settled: this.offsetsDone > 0,
    };
  }
}
